/**
 * DIME cloud web-api client.
 *
 * Auth model: every backend call presents a DIME JWT minted through the
 * host-supplied `DimeTokenExchange` (Clerk today; see `identity.ts`). The DIME
 * JWT has a 5-minute TTL and there is no refresh endpoint, so each call here
 * mints a fresh one immediately before the request (and re-mints + retries
 * exactly once on a 401). Nothing returned by this module may be persisted —
 * connection grants are memory-only by contract (`Cache-Control: no-store`).
 *
 * Never log the access code, the DIME JWT, or the grant token.
 */
import {
  DimeApiError,
  type DimeIdentity,
  type DimeTokenExchange,
} from './identity.js';
import {
  CREDENTIAL_ENVELOPE_ALG,
  sealCredential,
} from '@paradigm/terminal-core';

import { resolveDimeApiUrl, type DimeEnvironment } from './environments.js';
import {
  createDimeSessionCache,
  sessionCacheKey,
  type DimeSessionCache,
} from './sessionCache.js';

/**
 * Sidecar forward auth for `TerminalConfig.sidecar`: routes the model-catalog
 * and session-sync calls through the web-api's allowlisted `/v1/gateway/*`
 * forward, authenticated by a fresh-enough DIME JWT — the backend injects
 * the real gateway credential, so the browser never needs the gateway URL or
 * token for sidecar calls.
 */
export interface SidecarAuth {
  readonly baseUrl: string;
  readonly getAuthHeaders: () => Readonly<Record<string, string>> | null;
}

/**
 * The in-memory connection grant: everything the terminal needs to reach the
 * user's gateway. `token` is the grant's gateway token when present, otherwise
 * the DIME JWT that resolved the connection (per the /v1/connection contract).
 */
export interface ConnectionGrant {
  readonly gatewayUrl: string;
  readonly token: string;
  /**
   * True when `token` is the DIME JWT itself (gateway-proxy grant with no
   * gateway token). Such a token expires in 5 minutes, so the terminal must
   * mint a fresh one per handshake (`resolveHandshakeToken`) instead of
   * replaying this one on reconnect. False ⇒ `token` is a long-lived gateway
   * credential and must be presented as-is.
   */
  readonly usesDimeJwt: boolean;
}

export type ConnectionResolution =
  | { readonly kind: 'connected'; readonly grant: ConnectionGrant }
  /** 202 — gateway still provisioning; poll again in ~2s. */
  | { readonly kind: 'provisioning' }
  /** 404 — JWT said onboarded but the account is gone; show the code screen. */
  | { readonly kind: 'no-account' }
  /** 409 — failed/suspended; terminal error screen. */
  | { readonly kind: 'unavailable'; readonly status: string };

export type RedeemResolution =
  /**
   * 201 created, or 409 account-already-exists (treated as success). Carries
   * the identity that authorized the redeem so the follow-up connection
   * resolve can reuse it instead of re-exchanging.
   */
  | { readonly kind: 'redeemed'; readonly identity: DimeIdentity }
  /** 400/403 — bad code; show inline and let the user retry. */
  | { readonly kind: 'rejected'; readonly message: string };

/**
 * The public key a credential is sealed to, plus the namespace it must be bound
 * to — the whole of `GET /v1/credentials/public-key`.
 *
 * `namespace` is per-account and comes from the server: it identifies where this
 * user's credentials live, and `sealCredential` binds the ciphertext to it. It
 * must never be assembled client-side — a wrong value produces a credential
 * that stores successfully and cannot be opened when the agent needs it.
 */
export interface CredentialKey {
  readonly keyId: string;
  readonly alg: string;
  readonly publicKeyPem: string;
  readonly namespace: string;
}

/**
 * How a stored secret reaches the upstream request. `inject` (the default) needs
 * nothing from the agent — the proxy attaches the secret to matching requests.
 * `replace` is for what inject cannot express, and is the only mode with a
 * placeholder.
 */
export type CredentialDelivery =
  | {
      readonly mode: 'inject';
      readonly header?: string;
      readonly queryParam?: string;
      readonly formatter?: string;
    }
  | {
      readonly mode: 'replace';
      readonly matchHeaders?: readonly string[];
      readonly matchBody?: boolean;
      readonly matchPath?: boolean;
      readonly matchQuery?: boolean;
    };

/** One stored credential, as the server describes it. Never its value. */
export interface StoredCredential {
  readonly id: string;
  readonly label: string;
  /**
   * The string the agent writes where the credential belongs; the proxy swaps
   * it for the real value on the way out. Not a secret — that is the design —
   * but high-entropy, so guessing one is not a way to borrow another account's
   * egress.
   */
  readonly placeholder: string;
  readonly allowedHosts: readonly string[];
  /**
   * Request paths this credential is narrowed to, within those hosts. Empty
   * means any path, which is both the default and what every credential
   * enrolled before this field existed does.
   *
   * More than cosmetic on a replace-mode credential: the rule that matches is
   * also the rule that REQUIRES the placeholder, so a host with no path list
   * turns that host's public endpoints into refusals too.
   */
  readonly allowedPaths: readonly string[];
  readonly delivery: CredentialDelivery;
  readonly createdAt: string;
  /**
   * Whether the agent currently holds this credential's placeholder. `false`
   * means it is stored and the proxy will substitute it, but the agent has not
   * been told yet — the server republishes in the background, so this is a
   * transient state rather than something the user has to fix.
   * `undefined` means UNKNOWN: the agent could not be asked (a pod
   * mid-rollout), and no claim should be shown.
   */
  readonly agentReady?: boolean;
}

/** What the caller supplies to store a credential. */
export interface StoreCredentialInput {
  readonly label: string;
  readonly allowedHosts: readonly string[];
  /**
   * Request paths this credential is narrowed to, within those hosts. Empty
   * means any path, which is both the default and what every credential
   * enrolled before this field existed does.
   *
   * More than cosmetic on a replace-mode credential: the rule that matches is
   * also the rule that REQUIRES the placeholder, so a host with no path list
   * turns that host's public endpoints into refusals too.
   */
  readonly allowedPaths: readonly string[];
  readonly delivery: CredentialDelivery;
  /** The credential itself. Sealed before it leaves the browser. */
  readonly secret: string;
}

export type StoreCredentialResolution =
  | {
      readonly kind: 'stored';
      readonly credential: StoredCredential;
      /**
       * Whether the placeholder reached the user's agent. The server publishes
       * it into the agent's environment as part of enrolling, so this is
       * normally true; false means the credential is stored and the proxy will
       * substitute it, but the agent has not been told and the user has to
       * paste the placeholder until the next enrollment republishes the set.
       */
      readonly agentReady: boolean;
    }
  /**
   * The server rejected the request for a reason the user can act on: a label
   * already in use, a bad hostname, the per-account limit. `message` is
   * displayable as-is.
   */
  | { readonly kind: 'rejected'; readonly message: string };

/**
 * What the caller supplies to correct one. No label and no secret: the label is
 * half the AAD the stored ciphertext is sealed to, so renaming would leave a
 * credential nothing can open, and a new value means a new envelope (POST).
 */
export interface UpdateCredentialInput {
  readonly allowedHosts: readonly string[];
  /**
   * Request paths this credential is narrowed to, within those hosts. Empty
   * means any path, which is both the default and what every credential
   * enrolled before this field existed does.
   *
   * More than cosmetic on a replace-mode credential: the rule that matches is
   * also the rule that REQUIRES the placeholder, so a host with no path list
   * turns that host's public endpoints into refusals too.
   */
  readonly allowedPaths: readonly string[];
  readonly delivery: CredentialDelivery;
}

export type UpdateCredentialResolution =
  | {
      readonly kind: 'updated';
      readonly credential: StoredCredential;
      /**
       * Whether the agent was told. Only meaningful when the edit moved the
       * placeholder in or out of existence — a delivery change within replace
       * mode keeps the same placeholder, so there is nothing new to tell.
       */
      readonly agentReady: boolean;
    }
  | { readonly kind: 'rejected'; readonly message: string }
  /** 404 — revoked, never existed, or another account's. Indistinguishable. */
  | { readonly kind: 'not-found' };

export type RevokeCredentialResolution =
  | { readonly kind: 'revoked' }
  /** 404 — already gone, or never existed. Idempotent from the UI's side. */
  | { readonly kind: 'not-found' }
  /**
   * 502 — the credential IS revoked (no proxy will inject it again) but its
   * stored copy could not be deleted and needs an operator. Surfaced rather
   * than swallowed: the user asked us to forget it, and we partly have not.
   */
  | { readonly kind: 'partial'; readonly message: string };

export interface DimeCloudOptions {
  /** Named backend to target. Default `'production'`. */
  readonly environment?: DimeEnvironment;
  /** Explicit web-api base URL — wins over `environment`. */
  readonly apiUrl?: string;
  /** How this host obtains DIME JWTs (e.g. `createClerkTokenExchange(getToken)`). */
  readonly exchange: DimeTokenExchange;
  /**
   * Provider-held session cache (`DimeTerminalProvider`), so minted
   * identities and resolved grants survive surface remounts. Omit ⇒ a
   * private per-client cache (per-mount lifetime, the historical behavior).
   */
  readonly sessionCache?: DimeSessionCache;
  /** Account id scoping the cache key; identities never cross accounts. */
  readonly accountId?: string;
}

export interface DimeCloudClient {
  /** The resolved web-api base URL this client targets (no trailing slash). */
  readonly apiUrl: string;
  /**
   * The DIME identity for this session — cache-first (single-flight, reused
   * while >60s of life remains), minting via the host's exchange on a miss.
   */
  readonly mintIdentity: () => Promise<DimeIdentity>;
  /**
   * Key-bound view of the session cache's grant slot, for the connection
   * state machine: a remount rehydrates from a still-fresh grant instead of
   * re-resolving (and re-minting).
   */
  readonly grantCache: {
    readonly get: () => ConnectionGrant | null;
    readonly invalidate: () => void;
  };
  /**
   * GET /v1/connection — resolve how this user's client connects. Pass a
   * just-minted `identity` to skip re-exchanging for the first attempt.
   */
  readonly fetchConnection: (
    preMinted?: DimeIdentity,
  ) => Promise<ConnectionResolution>;
  /** POST /v1/account — redeem a one-time operator-issued access code. */
  readonly redeemAccessCode: (code: string) => Promise<RedeemResolution>;
  /**
   * Sidecar auth over the shared session cache: `getAuthHeaders` returns the
   * cached token while fresh, or null while a background mint fills the cache
   * (safe: session sync treats null as transient and retries, and the model
   * fetch falls back to the static catalog).
   */
  readonly createSidecarAuth: () => SidecarAuth;
  /**
   * GET /v1/credentials/public-key — the key to seal a credential to and the
   * namespace to bind it to. Cached for the client's lifetime: the key is
   * immutable and the namespace changes only with the account.
   */
  readonly fetchCredentialKey: () => Promise<CredentialKey>;
  /** GET /v1/credentials — the caller's stored credentials, metadata only. */
  readonly listCredentials: () => Promise<readonly StoredCredential[]>;
  /**
   * Seals a credential in the browser and POSTs the result to /v1/credentials.
   * The plaintext never leaves this call: sealing happens here, and only the
   * envelope is sent.
   */
  readonly storeCredential: (
    input: StoreCredentialInput,
  ) => Promise<StoreCredentialResolution>;
  /**
   * PATCH /v1/credentials/{id} — corrects where a credential may go and how it
   * gets there. Sends no ciphertext, so it needs no key fetch and no sealing.
   */
  readonly updateCredential: (
    id: string,
    input: UpdateCredentialInput,
  ) => Promise<UpdateCredentialResolution>;
  /** DELETE /v1/credentials/{id}. */
  readonly revokeCredential: (
    id: string,
  ) => Promise<RevokeCredentialResolution>;
  /**
   * Fresh-enough-per-handshake DIME JWT for
   * `TerminalConfig.resolveHandshakeToken` (gateway-proxy grants only — the
   * proxy verifies the JWT at every connect). Rides the shared session cache
   * (reuse while >60s left, single-flight); resolves null on a mint failure
   * so the terminal falls back to its stored credential (transient, not
   * fatal).
   */
  readonly createHandshakeTokenResolver: () => () => Promise<string | null>;
}

/**
 * The displayable half of an error response.
 *
 * The web-api answers with either `{code, message}` (errors the UI dispatches
 * on) or `{message}` (everything else), so both shapes are read. A response
 * with neither falls back to a generic line rather than showing the user a
 * status code.
 */
async function errorMessage(res: Response): Promise<string> {
  const body = (await res.json().catch(() => null)) as {
    message?: string;
  } | null;
  const message = body?.message;
  return typeof message === 'string' && message.length > 0
    ? message
    : 'That didn’t work — please try again.';
}

export function createDimeCloudClient(opts: DimeCloudOptions): DimeCloudClient {
  const apiUrl = resolveDimeApiUrl(opts);
  const { exchange } = opts;
  const cache = opts.sessionCache ?? createDimeSessionCache();
  const cacheKey = sessionCacheKey(apiUrl, opts.accountId ?? '');

  const mintIdentity = (): Promise<DimeIdentity> =>
    cache.getIdentity(cacheKey, () => exchange(apiUrl));

  /**
   * Call a DIME endpoint with a just-minted DIME JWT; on 401 (JWT rejected —
   * e.g. clock skew ate the 5-minute TTL) re-exchange and retry exactly once.
   * Returns the response plus the identity whose JWT authorized it, so callers
   * that need the JWT as a fallback gateway token get the one that worked.
   *
   * `identity` lets a caller that just minted one (e.g. the onboarding-check
   * exchange) reuse it for the first attempt instead of minting again; the
   * 401 retry always re-mints, so a stale hand-me-down still self-heals.
   */
  const authedFetch = async (
    path: string,
    init: Omit<RequestInit, 'headers'> & {
      readonly headers?: Record<string, string>;
    } = {},
    identity?: DimeIdentity,
  ): Promise<{ readonly res: Response; readonly identity: DimeIdentity }> => {
    const attempt = async (
      preMinted?: DimeIdentity,
    ): Promise<{
      res: Response;
      identity: DimeIdentity;
    }> => {
      const id = preMinted ?? (await mintIdentity());
      const res = await fetch(`${apiUrl}${path}`, {
        ...init,
        headers: {
          ...init.headers,
          Authorization: `Bearer ${id.token}`,
        },
      });
      return { res, identity: id };
    };

    const first = await attempt(identity);
    if (first.res.status !== 401) return first;
    // The JWT was rejected — a cached identity is poisoned; drop it so the
    // retry (and every other consumer) mints fresh.
    cache.invalidateIdentity(cacheKey);
    return attempt();
  };

  const fetchConnection = async (
    preMinted?: DimeIdentity,
  ): Promise<ConnectionResolution> => {
    const { res, identity } = await authedFetch(
      '/v1/connection',
      {},
      preMinted,
    );
    if (res.status === 200) {
      const body = (await res.json()) as { url?: string; token?: string };
      if (typeof body.url !== 'string' || body.url.length === 0) {
        throw new DimeApiError(
          200,
          'Connection grant is missing a gateway URL',
        );
      }
      // Contract: when the grant carries no gateway token, the client
      // presents the DIME JWT itself to the gateway.
      const hasGatewayToken =
        typeof body.token === 'string' && body.token.length > 0;
      const grant: ConnectionGrant = {
        gatewayUrl: body.url,
        token: hasGatewayToken ? (body.token as string) : identity.token,
        usesDimeJwt: !hasGatewayToken,
      };
      cache.setGrant(cacheKey, grant);
      return { kind: 'connected', grant };
    }
    // Any non-connected resolution supersedes a previously cached grant —
    // a remount must re-resolve, not rehydrate a stale positive.
    cache.invalidateGrant(cacheKey);
    if (res.status === 202) return { kind: 'provisioning' };
    if (res.status === 404) return { kind: 'no-account' };
    if (res.status === 409) {
      const body = (await res.json().catch(() => null)) as {
        status?: string;
      } | null;
      return { kind: 'unavailable', status: body?.status ?? 'failed' };
    }
    throw new DimeApiError(
      res.status,
      `Connection resolve failed (${res.status})`,
    );
  };

  const redeemAccessCode = async (code: string): Promise<RedeemResolution> => {
    const { res, identity } = await authedFetch('/v1/account', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });
    if (res.status === 201 || res.status === 409) {
      // The cached identity (if any) predates onboarding — its `onboarded`
      // claim is now stale. The redeem's own identity is threaded to the
      // follow-up resolve, so this costs nothing on the happy path.
      cache.invalidateIdentity(cacheKey);
      return { kind: 'redeemed', identity };
    }
    if (res.status === 403) {
      return {
        kind: 'rejected',
        message: 'Invalid access code — check it and try again.',
      };
    }
    if (res.status === 400) {
      return {
        kind: 'rejected',
        message: 'That access code can’t be used — check it and try again.',
      };
    }
    throw new DimeApiError(
      res.status,
      `Access-code redeem failed (${res.status})`,
    );
  };

  // ─── Credentials ──────────────────────────────────────────────────────────

  // Held for the client's lifetime rather than per call: the KMS public key is
  // immutable (a rotation is a new key id, not new material under the old one)
  // and the namespace is fixed by the account. Not persisted anywhere — a
  // remount refetches, which costs one request.
  let credentialKey: CredentialKey | null = null;

  const fetchCredentialKey = async (): Promise<CredentialKey> => {
    if (credentialKey != null) return credentialKey;
    const { res } = await authedFetch('/v1/credentials/public-key');
    if (!res.ok) {
      throw new DimeApiError(
        res.status,
        `Could not fetch the credential key (${res.status})`,
      );
    }
    const body = (await res.json()) as {
      key_id?: string;
      alg?: string;
      public_key_pem?: string;
      namespace?: string;
    };
    if (
      typeof body.key_id !== 'string' ||
      typeof body.alg !== 'string' ||
      typeof body.public_key_pem !== 'string' ||
      typeof body.namespace !== 'string' ||
      body.public_key_pem.length === 0 ||
      body.namespace.length === 0
    ) {
      // Sealing against a partial response would produce an envelope bound to
      // the wrong context, which fails only when the agent finally needs it.
      throw new DimeApiError(
        res.status,
        'Credential key response is incomplete',
      );
    }
    // The ALGORITHM, not just its presence. sealCredential implements exactly
    // one — RSA-OAEP-SHA256 wrapping an AES-256-GCM key — and the key import
    // accepts any RSA size, so a service publishing a different spec would be
    // sealed against happily here and refused by every opener later, at
    // injection time on a live request. This is checkable now, so it is
    // checked now.
    if (body.alg !== CREDENTIAL_ENVELOPE_ALG) {
      throw new DimeApiError(
        res.status,
        `This page cannot seal credentials for ${body.alg} (it implements ${CREDENTIAL_ENVELOPE_ALG}). Reload; if it persists, the service and this build disagree.`,
      );
    }
    credentialKey = {
      keyId: body.key_id,
      alg: body.alg,
      publicKeyPem: body.public_key_pem,
      namespace: body.namespace,
    };
    return credentialKey;
  };

  const toStoredCredential = (raw: unknown): StoredCredential => {
    const c = raw as {
      id?: string;
      label?: string;
      placeholder?: string;
      allowed_hosts?: string[];
      allowed_paths?: string[];
      created_at?: string;
      agent_ready?: boolean;
      delivery?: {
        mode?: string;
        header?: string;
        query_param?: string;
        formatter?: string;
        match_headers?: string[];
        match_body?: boolean;
        match_path?: boolean;
        match_query?: boolean;
      };
    };
    const d = c.delivery ?? {};
    const delivery: CredentialDelivery =
      d.mode === 'replace'
        ? {
            mode: 'replace',
            matchHeaders: d.match_headers ?? [],
            matchBody: d.match_body ?? false,
            matchPath: d.match_path ?? false,
            matchQuery: d.match_query ?? false,
          }
        : {
            mode: 'inject',
            header: d.header ?? '',
            queryParam: d.query_param ?? '',
            formatter: d.formatter ?? '',
          };
    return {
      id: c.id ?? '',
      label: c.label ?? '',
      placeholder: c.placeholder ?? '',
      allowedHosts: c.allowed_hosts ?? [],
      // Absent means no narrowing, which is what a server that predates this
      // field means too — so the fallback is the correct reading, not a guess.
      allowedPaths: c.allowed_paths ?? [],
      delivery,
      createdAt: c.created_at ?? '',
      // Absent stays undefined rather than becoming false: the server omits
      // it when it could not ask the agent, and "not there yet" is a reason
      // to act while "we could not ask" is not.
      agentReady: c.agent_ready,
    };
  };

  /** The wire shape of a delivery block: snake_case, only the mode's own keys. */
  const toDeliveryPayload = (d: CredentialDelivery): Record<string, unknown> =>
    d.mode === 'replace'
      ? {
          mode: 'replace',
          match_headers: d.matchHeaders ?? [],
          match_body: d.matchBody ?? false,
          match_path: d.matchPath ?? false,
          match_query: d.matchQuery ?? false,
        }
      : {
          mode: 'inject',
          header: d.header ?? '',
          query_param: d.queryParam ?? '',
          formatter: d.formatter ?? '',
        };

  const listCredentials = async (): Promise<readonly StoredCredential[]> => {
    const { res } = await authedFetch('/v1/credentials');
    if (!res.ok) {
      throw new DimeApiError(
        res.status,
        `Could not list credentials (${res.status})`,
      );
    }
    const body = (await res.json()) as unknown[];
    return Array.isArray(body) ? body.map(toStoredCredential) : [];
  };

  const storeCredential = async (
    input: StoreCredentialInput,
  ): Promise<StoreCredentialResolution> => {
    const key = await fetchCredentialKey();
    const envelope = await sealCredential({
      publicKeyPem: key.publicKeyPem,
      keyId: key.keyId,
      namespace: key.namespace,
      label: input.label,
      secret: input.secret,
    });

    const { res } = await authedFetch('/v1/credentials', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // The envelope, and nothing else derived from the credential. The
      // plaintext ends its life in the sealCredential call above.
      body: JSON.stringify({
        label: input.label,
        allowed_hosts: input.allowedHosts,
        allowed_paths: input.allowedPaths,
        delivery: toDeliveryPayload(input.delivery),
        envelope,
      }),
    });

    if (res.status === 201) {
      const body = (await res.json()) as { agent_ready?: boolean };
      return {
        kind: 'stored',
        credential: toStoredCredential(body),
        // Absent on a server that predates agent publishing. Treated as ready
        // rather than warning about a mechanism that environment does not have.
        agentReady: body.agent_ready !== false,
      };
    }
    // 400 (bad label/host/envelope), 403 (account not eligible), 409 (label
    // taken), 422 (limit) are all things the user can act on, and the server's
    // message says which. 502 means nothing was saved, so it reads the same way
    // to the user: try again.
    if (
      res.status === 400 ||
      res.status === 403 ||
      res.status === 409 ||
      res.status === 422 ||
      res.status === 502
    ) {
      return { kind: 'rejected', message: await errorMessage(res) };
    }
    throw new DimeApiError(
      res.status,
      `Storing the credential failed (${res.status})`,
    );
  };

  const updateCredential = async (
    id: string,
    input: UpdateCredentialInput,
  ): Promise<UpdateCredentialResolution> => {
    const { res } = await authedFetch(
      `/v1/credentials/${encodeURIComponent(id)}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          allowed_hosts: input.allowedHosts,
          allowed_paths: input.allowedPaths,
          delivery: toDeliveryPayload(input.delivery),
        }),
      },
    );

    if (res.status === 200) {
      const body = (await res.json()) as { agent_ready?: boolean };
      return {
        kind: 'updated',
        credential: toStoredCredential(body),
        agentReady: body.agent_ready !== false,
      };
    }
    if (res.status === 404) return { kind: 'not-found' };
    // Same set as storing, minus the ones that cannot apply: no label to clash
    // (409), no cap to hit (422), no vault write to fail (502).
    if (res.status === 400 || res.status === 403) {
      return { kind: 'rejected', message: await errorMessage(res) };
    }
    throw new DimeApiError(
      res.status,
      `Updating the credential failed (${res.status})`,
    );
  };

  const revokeCredential = async (
    id: string,
  ): Promise<RevokeCredentialResolution> => {
    const { res } = await authedFetch(
      `/v1/credentials/${encodeURIComponent(id)}`,
      {
        method: 'DELETE',
      },
    );
    if (res.status === 204) return { kind: 'revoked' };
    if (res.status === 404) return { kind: 'not-found' };
    if (res.status === 502) {
      return { kind: 'partial', message: await errorMessage(res) };
    }
    throw new DimeApiError(
      res.status,
      `Revoking the credential failed (${res.status})`,
    );
  };

  // Both token consumers ride the shared session cache — freshness slack,
  // single-flighting and cross-consumer reuse all live there. No private
  // caches, no eager mint: the bootstrap/silent exchange has usually
  // populated the cache before either consumer first asks.
  const createSidecarAuth = (): SidecarAuth => ({
    baseUrl: `${apiUrl}/v1/gateway`,
    getAuthHeaders: () => {
      const token = cache.peekToken(cacheKey);
      if (token == null) {
        // Sync contract: null now (session sync treats it as transient and
        // retries; the model fetch falls back to the static catalog) while a
        // background mint fills the cache for the next call.
        void mintIdentity().catch(() => {});
        return null;
      }
      return { Authorization: `Bearer ${token}` };
    },
  });

  const createHandshakeTokenResolver =
    (): (() => Promise<string | null>) => () =>
      mintIdentity()
        .then((id) => id.token)
        // Null on a mint failure so the terminal falls back to its stored
        // credential (transient, not fatal).
        .catch(() => null);

  return {
    apiUrl,
    mintIdentity,
    grantCache: {
      get: () => cache.getGrant(cacheKey),
      invalidate: () => cache.invalidateGrant(cacheKey),
    },
    fetchConnection,
    redeemAccessCode,
    fetchCredentialKey,
    listCredentials,
    storeCredential,
    updateCredential,
    revokeCredential,
    createSidecarAuth,
    createHandshakeTokenResolver,
  };
}
